import { memo, useCallback } from 'react';

interface FilterToggleProps {
  label: string;
  value: boolean;
  onChange: (next: boolean) => void;
  /** Optional Material icon name to render before the label. */
  icon?: string;
  /** Render as a pill (default) or a switch row (mobile). */
  variant?: 'pill' | 'row';
  ariaLabel?: string;
}

/**
 * Boolean filter as a pill toggle (desktop) or row with switch (mobile).
 */
export const FilterToggle = memo(function FilterToggle({
  label,
  value,
  onChange,
  icon,
  variant = 'pill',
  ariaLabel,
}: FilterToggleProps) {
  const handle = useCallback(() => onChange(!value), [onChange, value]);

  if (variant === 'row') {
    return (
      <div className="filter-toggle-row">
        <label className="filter-toggle-row-label" htmlFor={`toggle-${label}`}>
          {icon && (
            <span className="material-symbols-outlined" aria-hidden="true">{icon}</span>
          )}
          <span>{label}</span>
        </label>
        <button
          id={`toggle-${label}`}
          type="button"
          role="switch"
          aria-checked={value}
          aria-label={ariaLabel ?? label}
          className={`filter-toggle-switch ${value ? 'on' : ''}`}
          onClick={handle}
        >
          <span className="filter-toggle-switch-thumb" />
        </button>
      </div>
    );
  }

  return (
    <button
      type="button"
      role="switch"
      aria-checked={value}
      aria-label={ariaLabel ?? label}
      className={`filter-toggle-pill ${value ? 'on' : ''}`}
      onClick={handle}
    >
      {icon && (
        <span className="material-symbols-outlined" aria-hidden="true">{icon}</span>
      )}
      <span>{label}</span>
    </button>
  );
});

export default FilterToggle;
